import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/vue-query";

import { useFilters } from "@/hooks/filters";
import stipsApiService from "@/services/modules/stips";

import type { IPaginatedResponse } from "@/models/common";
import type { StipDefinition } from "@/models/stips";
import type { Ref } from "vue";

type UpdateStipMutationParams = {
  id: StipDefinition["id"];
  stip: Partial<StipDefinition>;
};

export const STIPS_QUERY_KEYS = {
  ROOT: "stips",
  INDEX: "stips-index",
  INFINITE: "stips-infinite",
} as const;

export const useStipsIndexQuery = (
  searchParams?: Ref<Record<string, unknown>>
) => {
  return useQuery({
    queryKey: [STIPS_QUERY_KEYS.ROOT, STIPS_QUERY_KEYS.INDEX, searchParams],
    queryFn: async () => {
      return await stipsApiService.getStips(searchParams?.value);
    },
    staleTime: Infinity,
  });
};

export const useStipsInfiniteQuery = (
  searchParams?: Ref<Record<string, unknown>>
) => {
  const perPage = 200;

  return useInfiniteQuery({
    queryKey: [STIPS_QUERY_KEYS.ROOT, STIPS_QUERY_KEYS.INFINITE, searchParams],
    queryFn: async ({ pageParam = 1 }) => {
      return await stipsApiService.getStips({
        ...searchParams?.value,
        page: pageParam,
        per_page: perPage,
      });
    },
    getNextPageParam: (lastPage) => {
      const { current_page, last_page } = lastPage.meta;
      return current_page < last_page ? current_page + 1 : undefined;
    },
    initialPageParam: 1,
  });
};

export const useCreateStipMutation = () => {
  const { filterStateAsSearchParams: searchParams } = useFilters();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (stip: Partial<StipDefinition>) => {
      const createdStip = await stipsApiService.createStip(stip);

      // The created stip should always appear at the top of the table after creation.
      await queryClient.setQueryData(
        [STIPS_QUERY_KEYS.ROOT, STIPS_QUERY_KEYS.INDEX, searchParams],
        (oldData: IPaginatedResponse<StipDefinition>) => {
          if (oldData?.data) {
            return {
              ...oldData,
              data: [createdStip, ...oldData.data],
            };
          }
          return oldData;
        }
      );

      queryClient.invalidateQueries({
        queryKey: [STIPS_QUERY_KEYS.INFINITE],
      });

      return createdStip;
    },
  });
};

export const useUpdateStipMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, stip }: UpdateStipMutationParams) => {
      await stipsApiService.updateStip(id, stip);

      queryClient.invalidateQueries({
        queryKey: [STIPS_QUERY_KEYS.ROOT],
      });
    },
  });
};

export const useDeleteStipMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (stipId: StipDefinition["id"]) => {
      await stipsApiService.deleteStip(stipId);

      queryClient.invalidateQueries({
        queryKey: [STIPS_QUERY_KEYS.ROOT],
      });
    },
  });
};
